import { ChangeEvent, useCallback, useMemo, useState } from 'react';

import {
  MAX_DAY,
  MIN_YEAR,
  formatDateString,
  getDaysInMonth,
  getMonthNames,
  isValidDay,
  isValidMonth,
  isValidYear,
  padNumber,
  parseDateString,
} from '@foundation/utils/dateUtils';

interface UseDateInputControllerProps {
  defaultValue?: string | number | readonly string[];
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  name?: string;
  locale?: string;
}

interface Suggestion {
  value: string;
  label: string;
}

export function useDateInputController({
  defaultValue,
  onChange,
  name,
  locale,
}: UseDateInputControllerProps) {
  const MONTHS = useMemo(() => getMonthNames(locale), [locale]);
  const currentYear = new Date().getFullYear();

  const initialDate = useMemo(
    () => parseDateString(defaultValue),
    [defaultValue],
  );

  const [daySearch, setDaySearch] = useState(initialDate.day);
  const [monthSearch, setMonthSearch] = useState(initialDate.month);
  const [yearSearch, setYearSearch] = useState(initialDate.year);

  const year = useMemo(() => {
    const trimmed = yearSearch.trim();
    if (!trimmed) return '';

    const yearNum = parseInt(trimmed);
    return isValidYear(yearNum, MIN_YEAR, currentYear)
      ? yearNum.toString()
      : '';
  }, [yearSearch, currentYear]);

  const month = useMemo(() => {
    const trimmed = monthSearch.trim();
    if (!trimmed) return '';

    const lowerValue = trimmed.toLowerCase();
    const monthNum = parseInt(trimmed);

    if (isValidMonth(monthNum)) {
      return padNumber(monthNum);
    }

    const foundIndex = MONTHS.findIndex(
      (m) =>
        m.toLowerCase() === lowerValue ||
        m.toLowerCase().startsWith(lowerValue),
    );
    return foundIndex >= 0 ? padNumber(foundIndex + 1) : '';
  }, [monthSearch, MONTHS]);

  const day = useMemo(() => {
    const trimmed = daySearch.trim();
    if (!trimmed) return '';

    const dayNum = parseInt(trimmed);
    const maxDaysForValidation =
      month && year ? getDaysInMonth(parseInt(month), parseInt(year)) : MAX_DAY;
    return isValidDay(dayNum, maxDaysForValidation) ? padNumber(dayNum) : '';
  }, [daySearch, month, year]);

  const maxDays = useMemo(
    () =>
      month && year ? getDaysInMonth(parseInt(month), parseInt(year)) : MAX_DAY,
    [month, year],
  );

  const updateDate = useCallback(
    (newDay: string, newMonth: string, newYear: string) => {
      const dateString = formatDateString(newDay, newMonth, newYear);
      const event = {
        target: { value: dateString, name },
      } as ChangeEvent<HTMLInputElement>;
      onChange?.(event);
    },
    [onChange, name],
  );

  const adjustDayIfNeeded = useCallback(
    (currentDay: string, targetMonth: string, targetYear: string): string => {
      if (!currentDay || !targetMonth || !targetYear) return currentDay;

      const maxDaysInMonth = getDaysInMonth(
        parseInt(targetMonth),
        parseInt(targetYear),
      );
      const dayNum = parseInt(currentDay);

      if (dayNum > maxDaysInMonth) {
        return padNumber(maxDaysInMonth);
      }
      return currentDay;
    },
    [],
  );

  const handleDayBlur = useCallback(() => {
    const trimmed = daySearch.trim();

    if (!trimmed) {
      setDaySearch('');
      updateDate('', month, year);
      return;
    }

    const dayNum = parseInt(trimmed);
    if (isValidDay(dayNum, maxDays)) {
      const padded = padNumber(dayNum);
      setDaySearch(padded);
      updateDate(padded, month, year);
    } else {
      setDaySearch('');
      updateDate('', month, year);
    }
  }, [daySearch, month, year, maxDays, updateDate]);

  const handleDayChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setDaySearch(value);
      const dayNum = parseInt(value.trim());
      if (value.trim() && isValidDay(dayNum, maxDays)) {
        const padded = padNumber(dayNum);
        updateDate(padded, month, year);
      } else if (!value.trim()) {
        updateDate('', month, year);
      }
    },
    [month, year, maxDays, updateDate],
  );

  const handleMonthChange = useCallback(
    (value: string) => {
      setMonthSearch(value);

      const adjustedDay = adjustDayIfNeeded(day, value, year);
      setDaySearch(adjustedDay);

      updateDate(adjustedDay, value, year);
    },
    [day, year, adjustDayIfNeeded, updateDate],
  );

  const handleYearBlur = useCallback(() => {
    const trimmed = yearSearch.trim();

    if (!trimmed) {
      setYearSearch('');
      updateDate(day, month, '');
      return;
    }

    const yearNum = parseInt(trimmed);
    if (isValidYear(yearNum, MIN_YEAR, currentYear)) {
      const yearStr = yearNum.toString();

      setYearSearch(yearStr);

      const adjustedDay = adjustDayIfNeeded(day, month, yearStr);
      setDaySearch(adjustedDay);

      updateDate(adjustedDay, month, yearStr);
    } else {
      setYearSearch('');
      updateDate(day, month, '');
    }
  }, [yearSearch, day, month, currentYear, updateDate, adjustDayIfNeeded]);

  const handleYearChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setYearSearch(value);

      const yearNum = parseInt(value.trim());
      if (value.trim() && isValidYear(yearNum, MIN_YEAR, currentYear)) {
        const yearStr = yearNum.toString();
        const adjustedDay = adjustDayIfNeeded(day, month, yearStr);
        setDaySearch(adjustedDay);
        updateDate(adjustedDay, month, yearStr);
      } else if (!value.trim()) {
        updateDate(day, month, '');
      }
    },
    [day, month, currentYear, adjustDayIfNeeded, updateDate],
  );

  const monthOptions = useMemo((): Suggestion[] => {
    return MONTHS.map((label, i) => ({
      value: padNumber(i + 1),
      label,
    }));
  }, [MONTHS]);

  const resultDate = useMemo(
    () => formatDateString(day, month, year),
    [day, month, year],
  );

  return {
    day,
    month,
    year,

    daySearch,
    monthSearch,
    yearSearch,
    resultDate,

    setDaySearch,
    setMonthSearch,
    setYearSearch,

    handleDayChange,
    handleDayBlur,
    handleMonthChange,
    handleYearChange,
    handleYearBlur,

    monthOptions,
  };
}
